import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AuthContext,
  type AuthContextValue,
} from "@/contexts/auth-context";
import { getLoginMethods, getMe } from "@/lib/api/auth";
import { setAuthFlash } from "@/lib/auth-flash";
import {
  clearTokens,
  getAccessToken,
  setTokens,
} from "@/lib/auth-storage";
import { MOCK_API } from "@/lib/constants";
import { isMockToken, userFromMockToken } from "@/lib/mock/store";
import type { User } from "@/types";

function isSsoCallbackPath() {
  return typeof window !== "undefined" && window.location.pathname === "/sso-callback";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // Drop any leftover JWT before the SSO exchange starts. Otherwise a stale
  // /api/auth/me 401 can clearTokens() after the new JWT is written and bounce
  // the user to /login with no error UI.
  const [hasToken, setHasToken] = useState(() => {
    if (isSsoCallbackPath()) {
      clearTokens();
      return false;
    }
    return Boolean(getAccessToken());
  });

  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: getMe,
    enabled: hasToken,
    retry: false,
    staleTime: 60_000,
    initialData: () => {
      if (!MOCK_API) return undefined;
      const token = getAccessToken();
      if (!isMockToken(token)) return undefined;
      return userFromMockToken(token) ?? undefined;
    },
  });

  useEffect(() => {
    if (!meQuery.isError || !hasToken) return;
    clearTokens();
    setHasToken(false);
    setAuthFlash("Your session expired. Please sign in again.");
    if (!isSsoCallbackPath()) {
      window.location.href = "/login";
    }
  }, [hasToken, meQuery.isError]);

  const login = useCallback(
    (accessToken: string, refreshToken: string, user?: User) => {
      void queryClient.cancelQueries({ queryKey: ["auth", "me"] });
      // flushSync so navigate('/') after SSO sees hasToken + user on the same turn.
      // Without it, _app treats the session as logged-out and sends the user to /login.
      flushSync(() => {
        setTokens(accessToken, refreshToken);
        setHasToken(true);
        if (user) {
          queryClient.setQueryData(["auth", "me"], user);
        }
      });
      if (!user) {
        void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      }
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    clearTokens();
    setHasToken(false);
    queryClient.removeQueries({ queryKey: ["auth"] });
    queryClient.clear();

    if (MOCK_API) {
      window.location.href = "/login";
      return;
    }

    void (async () => {
      try {
        const methods = await getLoginMethods();
        const nexusUrl = methods.nexus_login_url?.trim();
        if (nexusUrl) {
          // Logout: bare Nexus login (no returnUrl) so the user stays on SSO.
          window.location.href = nexusUrl;
          return;
        }
      } catch {
        // Fall through to local login if Nexus URL is unavailable.
      }
      window.location.href = "/login";
    })();
  }, [queryClient]);

  const refetchUser = useCallback(async () => {
    await meQuery.refetch();
  }, [meQuery]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isLoading: hasToken && meQuery.isLoading,
      isAuthenticated: hasToken && Boolean(meQuery.data),
      login,
      logout,
      refetchUser,
    }),
    [hasToken, login, logout, meQuery.data, meQuery.isLoading, refetchUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
